import { useFormik } from 'formik';
import React, { Fragment, useEffect } from 'react';
import { FaEuroSign, FaPercent } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { Input, Switch } from 'rizzui';
import CustomSelect from '../../components/forms/CustomSelect';
import CustomSelectApiHookForm from '../../components/forms/CustomSelectApiHookForms';
import RequiredInput from '../../components/forms/InputHOC';
import useContract from '../../hooks/api-calls/contract/useContracts';
import usePaymentMethods from '../../hooks/api-calls/contract/usePaymentMethods';
import useInvoiceClients from '../../hooks/api-calls/invoices/useInvoiceClients';
import useInvoiceCorporations from '../../hooks/api-calls/invoices/useInvoiceCorporations';
import useInvoiceTutors from '../../hooks/api-calls/invoices/useInvoiceTutors';
import Page from '../../layout/Page/Page';
import FormGroup from '../../layout/shared/form-group';
import { InvoiceService } from '../../services/invoice/invoiceService';
import { Invoice } from '../../type/entities/invoice-type';
import FormFooter from '../_layout/_footers/form-footer';
import InvoiceLinesForm from './components/InvoiceLinesForm';
import { externalClientNifValidation, externalClientPostalCodeValidation } from './utils/externalClientValidation';
import { fixNumber } from '../../utils/formatNumber';
import { IVA_NON_SUBJECT_LEGAL_TEXT } from './utils/ivaTerritory';

const RECEIVER_TYPE_OPTIONS = [
    { value: 'system_client', label: 'Cliente del sistema' },
    { value: 'fiscal_company', label: 'Empresa fiscal del sistema' },
    { value: 'external_client', label: 'Cliente externo' },
];

interface CreateFormProps {
    isLoading: boolean;
    submit: Function;
    data?: Invoice | undefined;
}

const schema = yup.object({
    taxableAmount: yup.number().required('Campo obligatorio'),
    tax: yup.number().required('Campo obligatorio'),
    totalTaxesAmount: yup.number().required('Campo obligatorio'),
    totalProducts: yup.number().required('Campo obligatorio'),
    currentStatusId: yup.string().required('Campo obligatorio'),
    totalPrice: yup.number().required('Campo obligatorio'),
    invoiceDate: yup.date().required('Campo obligatorio'),
    expirationDate: yup.date().required('Campo obligatorio'),
    paymentMethodId: yup.string().required('Campo obligatorio'),
    corporationId: yup.string().required('Campo obligatorio'),
    providingCorporationId: yup.string().nullable().notRequired(),
    contractId: yup.string().notRequired().nullable(),
    lines: yup.array().of(
        yup.object().shape({
            contract: yup.mixed().notRequired().nullable(),
            concept: yup.string().when('contract', {
                is: (value: any) => (value === null || value === undefined || value === ''),
                then: (schema) => schema.required('Campo obligatorio'),
                otherwise: (schema) => schema.notRequired(),
            }),
            amount: yup.number().when('contract', {
                is: (value: any) => value === null || value === undefined || value === '',
                then: (schema) => schema
                    .required('Campo obligatorio')
                    .min(0, 'El valor debe ser mayor o igual a 0')
                    .test(
                        'maxDecimals',
                        'Máximo 2 decimales permitidos',
                        (value) => !value || /^\d+(\.\d{1,2})?$/.test(value.toString())
                    ),
                otherwise: (schema) => schema.notRequired(),
            }),
            tax: yup.number().when('contract', {
                is: (value: any) => value === null || value === undefined || value === '',
                then: (schema) => schema
                    .required('Campo obligatorio')
                    .min(0, 'El valor debe ser mayor o igual a 0')
                    .max(100, 'El valor debe ser menor o igual a 100')
                    .test(
                        'maxDecimals',
                        'Máximo 2 decimales permitidos',
                        (value) => !value || /^\d+(\.\d{1,2})?$/.test(value.toString())
                    ),
                otherwise: (schema) => schema.notRequired(),
            }),
            units: yup.number().when('contract', {
                is: (value: any) => value === null || value === undefined || value === '',
                then: (schema) => schema
                    .required('Campo obligatorio')
                    .min(0, 'El valor debe ser mayor o igual a 0')
                    .test(
                        'isInteger',
                        'El valor debe ser un número entero',
                        (value) => !value || Number.isInteger(value)
                    ),
                otherwise: (schema) => schema.notRequired(),
            }),
            totalWithoutTax: yup.number().when('contract', {
                is: (value: any) => value === null || value === undefined || value === '',
                then: (schema) => schema
                    .required('Campo obligatorio')
                    .min(0, 'El valor debe ser mayor o igual a 0')
                    .test(
                        'maxDecimals',
                        'Máximo 2 decimales permitidos',
                        (value) => !value || /^\d+(\.\d{1,2})?$/.test(value.toString())
                    ),
                otherwise: (schema) => schema.notRequired(),
            }),
            totalWithTax: yup.number().when('contract', {
                is: (value: any) => value === null || value === undefined || value === '',
                then: (schema) => schema
                    .required('Campo obligatorio')
                    .min(0, 'El valor debe ser mayor o igual a 0')
                    .test(
                        'maxDecimals',
                        'Máximo 2 decimales permitidos',
                        (value) => !value || /^\d+(\.\d{1,2})?$/.test(value.toString())
                    ),
                otherwise: (schema) => schema.notRequired(),
            }),
        })
    ).notRequired(),
    receiverType: yup.string().nullable().notRequired(),
    externalClientNif: externalClientNifValidation,
    externalClientPostalCode: externalClientPostalCodeValidation,
});

const InvoiceForm: React.FC<CreateFormProps> = ({ isLoading, submit, data }) => {

    const { id = '' } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const mode = data ? 'Editar' : 'Crear';

    const [providingCorporationsData, setProvidingCorporationsData] = React.useState<any[]>([]);

    const { getInvoiceCorporationsList } = useInvoiceCorporations({ active: true });
    const { getInvoiceTutorsList } = useInvoiceTutors({ active: true });
    const { fetchInvoiceClients } = useInvoiceClients({ active: true });
    const { fetchContracts } = useContract({ active: true }, true);
    const { getPaymentMethodsList } = usePaymentMethods();

    const fetchInvoiceCorporations = async (parentCorporationId: string) => {
        if (!parentCorporationId) return;
        try {
            const response = await (new InvoiceService()).getInvoiceProvidingCorporations(parentCorporationId);
            const fetchedData = response.getResponseData()?.data;

            if (fetchedData && fetchedData?.data) {
                const mappedData = fetchedData.data?.map((data: { id: string; name: string; }) => ({
                    value: data.id,
                    label: data.name,
                }));
                setProvidingCorporationsData(mappedData);
            }
        } catch (error) {
            console.log('Error fetching invoice providing corporations:', error);
        }
    };

    const isNonStandard = data?.invoiceType === 'non_standard_invoice';

    const inferReceiverType = () => {
        if (!data) return '';
        if (data.externalClientName) return 'external_client';
        if (data.providingCorporation && data.invoiceType === 'non_standard_invoice') return 'fiscal_company';
        if (data.client) return 'system_client';
        return '';
    };

    const formik = useFormik({
        initialValues: {
            invoiceId: id,
            taxableAmount: fixNumber(data?.taxableAmount || 0) || '',
            tax: data?.tax ?? '',
            totalTaxesAmount: fixNumber(data?.totalTaxesAmount || 0) ?? '',
            totalProducts: data?.totalProducts ?? '',
            totalPrice: fixNumber(data?.totalPrice || 0) ?? '',
            invoiceDate: data?.invoiceDate?.date?.split(' ')[0] || null,
            paymentDate: data?.paymentDate?.date?.split(' ')[0] || null,
            expirationDate: data?.expirationDate?.date?.split(' ')[0] || null,
            paymentMethodId: data?.paymentMethod?.id ?? data?.contract?.paymentMethods?.id ?? '',
            company: data?.company?.id ?? '',
            corporationId: data?.corporation?.id ?? '',
            providingCorporationId: data?.providingCorporation?.id ?? '',
            tutorId: data?.tutor?.id ?? null,
            clientId: data?.client?.id ?? null,
            clientName: data?.client?.companyName ?? '',
            currentStatusId: data?.currentStatus?.id ?? '',
            contractId: data?.contract?.id ?? null,
            // Non-standard invoice fields
            receiverType: inferReceiverType(),
            irpf: data?.irpf ?? null,
            irc: data?.irc ?? null,
            ivaExempt: data?.ivaExempt ?? false,
            externalClientName: data?.externalClientName ?? '',
            externalClientNif: data?.externalClientNif ?? '',
            externalClientAddress: data?.externalClientAddress ?? '',
            externalClientProvince: data?.externalClientProvince ?? '',
            externalClientLocality: data?.externalClientLocality ?? '',
            externalClientPostalCode: data?.externalClientPostalCode ?? '',
            externalClientContact: data?.externalClientContact ?? '',
            lines: data?.lines?.map((line: any) => ({
                id: line.id || '',
                contract: line.contract?.id || '',
                concept: line.concept || '',
                amount: line.amount || '',
                tax: line.tax || '',
                units: line.units || '',
                totalWithoutTax: fixNumber(line.totalWithoutTax) || '',
                totalWithTax: fixNumber(line.totalWithTax) || '',
            })) || [],
        },
        validationSchema: schema,
        validateOnBlur: false,
        //validateOnChange: false,
        onSubmit: values => { submit(values); },
    });

    useEffect(() => {
        if (formik.values.corporationId) {
            fetchInvoiceCorporations(formik.values.corporationId);
        }
    }, [formik.values.corporationId]);

    const getContent = () => {
        return (
            <>
                <div className="@container">
                    <form onSubmit={formik.handleSubmit}>
                        <FormGroup
                            title="Información General"
                            description="Datos principales de la factura"
                            className='pt-3 pb-4'
                            titleCols="@md:col-span-2"
                            childCols="@md:col-span-10 md:grid-cols-6"
                        >
                            <RequiredInput
                                id='totalProducts'
                                type="number"
                                label="Total Productos"
                                formik={formik}
                            />

                            <RequiredInput
                                id='tax'
                                type="number"
                                label="Impuesto"
                                suffix={<FaPercent />}
                                formik={formik}
                            />

                            <RequiredInput
                                id='taxableAmount'
                                type="number"
                                label="Base Imponible"
                                suffix={<FaEuroSign />}
                                formik={formik}
                            />

                            <RequiredInput
                                id='totalTaxesAmount'
                                type="number"
                                label="Total Impuestos"
                                suffix={<FaEuroSign />}
                                formik={formik}
                            />

                            <RequiredInput
                                id='totalPrice'
                                type="number"
                                label="Precio Total"
                                suffix={<FaEuroSign />}
                                formik={formik}
                            />

                            {!isNonStandard && (
                                <CustomSelectApiHookForm
                                    id="contractId"
                                    label="Contrato"
                                    formik={formik}
                                    fetchOptions={async (searchText: string | null) => {
                                        return fetchContracts({ search_text: searchText }) as any;
                                    }}
                                    error={formik.errors.contractId}
                                    isDisabled={mode === "Editar"}
                                    minInputLength={5}
                                />
                            )}
                        </FormGroup>

                        <FormGroup
                            title="Empresas y Usuarios"
                            description="Datos de las empresas fiscales y usuarios relacionados"
                            className='pt-3 pb-4'
                            titleCols="@md:col-span-2"
                            childCols="@md:col-span-10 md:grid-cols-2"
                        >
                            <CustomSelect
                                isSearchable
                                isClearable
                                id={'corporationId'}
                                label="Empresa Fiscal"
                                value={{ value: formik.values.corporationId, label: getInvoiceCorporationsList()?.find((g: any) => g.value === formik.values.corporationId)?.label }}
                                options={getInvoiceCorporationsList()}
                                onChange={(e: any) => { formik.setFieldValue('corporationId', e?.value) }}
                                required
                                error={formik.errors.corporationId}
                            />

                            <CustomSelect
                                isSearchable
                                isClearable
                                id={'providingCorporationId'}
                                label="Organización Impartidora"
                                value={{ value: formik.values.providingCorporationId, label: getInvoiceCorporationsList()?.find((g: any) => g.value === formik.values.providingCorporationId)?.label }}
                                options={providingCorporationsData}
                                onChange={(e: any) => { formik.setFieldValue('providingCorporationId', e?.value) }}
                                error={formik.errors.providingCorporationId}
                                isDisabled={!formik.values.corporationId || providingCorporationsData.length === 0}
                            />

                            <CustomSelect
                                isSearchable
                                isClearable
                                id={'tutorId'}
                                label="Tutor"
                                value={{ value: formik.values.tutorId, label: getInvoiceTutorsList()?.find((g: any) => g.value === formik.values.tutorId)?.label }}
                                options={getInvoiceTutorsList()}
                                onChange={(e: any) => { formik.setFieldValue('tutorId', e?.value) }}
                                error={formik.errors.tutorId}
                            />

                            <CustomSelectApiHookForm
                                id="clientId"
                                label="Cliente"
                                formik={formik}
                                fetchOptions={async (searchText: string | null) => {
                                    const text = searchText || formik.values.clientName;
                                    return fetchInvoiceClients({ search_text: text });
                                }}
                                error={formik.errors.clientId}
                                minInputLength={3}
                            />
                        </FormGroup>

                        <FormGroup
                            title="Fechas y Plazos"
                            description="Fechas de la factura y plazos de pago"
                            className='pt-3 pb-4'
                            titleCols="@md:col-span-2"
                            childCols="@md:col-span-10 md:grid-cols-3"
                        >
                            <RequiredInput
                                id='invoiceDate'
                                type="date"
                                label="Fecha Factura"
                                formik={formik}
                            />

                            <Input
                                id='paymentDate'
                                type="date"
                                label="Fecha Pago"
                                value={formik.values.paymentDate ?? ''}
                                onChange={(e: any) => formik.setFieldValue('paymentDate', e.target.value)}
                            />

                            <RequiredInput
                                id='expirationDate'
                                type="date"
                                label="Fecha Vencimiento"
                                formik={formik}
                            />

                            <CustomSelect
                                isSearchable
                                isClearable
                                id={'paymentMethodId'}
                                label="Método de Pago"
                                required
                                options={getPaymentMethodsList()}
                                onChange={(e: any) => formik.setFieldValue('paymentMethodId', e ? e.value : '')}
                                value={formik.values.paymentMethodId ? { value: formik.values.paymentMethodId, label: getPaymentMethodsList().find((item: any) => item.value === formik.values.paymentMethodId)?.label } : null}
                                error={formik.errors.paymentMethodId}
                                isDisabled={!isNonStandard && !!(data?.contract?.id || formik.values.contractId)}
                            />

                        </FormGroup>

                        <InvoiceLinesForm formik={formik} data={data} manualLinesOnly={isNonStandard} />

                        {isNonStandard && (
                            <>
                                <FormGroup
                                    title="Receptor"
                                    description="Tipo y datos del receptor de la factura atípica"
                                    className='pt-3 pb-4'
                                    titleCols="@md:col-span-2"
                                    childCols="@md:col-span-10 md:grid-cols-2"
                                >
                                    <div className="col-span-full">
                                        <CustomSelect
                                            isSearchable={false}
                                            isClearable={false}
                                            id="receiverType"
                                            label="Tipo de receptor"
                                            required
                                            value={RECEIVER_TYPE_OPTIONS.find(o => o.value === formik.values.receiverType) ?? null}
                                            options={RECEIVER_TYPE_OPTIONS}
                                            onChange={(e: any) => formik.setFieldValue('receiverType', e?.value ?? '')}
                                            error={formik.errors.receiverType as string}
                                        />
                                    </div>

                                    {formik.values.receiverType === 'system_client' && (
                                        <div className="col-span-full">
                                            <CustomSelectApiHookForm
                                                id="clientId"
                                                label="Cliente"
                                                formik={formik}
                                                fetchOptions={async (searchText: string | null) => {
                                                    const text = searchText || formik.values.clientName;
                                                    return fetchInvoiceClients({ search_text: text });
                                                }}
                                                error={formik.errors.clientId as string}
                                                minInputLength={3}
                                            />
                                        </div>
                                    )}

                                    {formik.values.receiverType === 'fiscal_company' && (
                                        <div className="col-span-full">
                                            <CustomSelect
                                                isSearchable
                                                isClearable
                                                id="providingCorporationId"
                                                label="Empresa fiscal receptora"
                                                required
                                                value={{ value: formik.values.providingCorporationId, label: getInvoiceCorporationsList()?.find((g: any) => g.value === formik.values.providingCorporationId)?.label }}
                                                options={getInvoiceCorporationsList()?.filter((c: any) => c.value !== formik.values.corporationId)}
                                                onChange={(e: any) => formik.setFieldValue('providingCorporationId', e?.value ?? null)}
                                                error={formik.errors.providingCorporationId as string}
                                            />
                                        </div>
                                    )}

                                    {formik.values.receiverType === 'external_client' && (
                                        <>
                                            <RequiredInput id="externalClientName" type="text" label="Nombre o razón social" formik={formik} />
                                            <RequiredInput id="externalClientNif" type="text" label="NIF / CIF" formik={formik} />
                                            <RequiredInput id="externalClientAddress" type="text" label="Dirección" formik={formik} />
                                            <RequiredInput id="externalClientProvince" type="text" label="Provincia" formik={formik} />
                                            <RequiredInput id="externalClientLocality" type="text" label="Localidad" formik={formik} />
                                            <RequiredInput id="externalClientPostalCode" type="text" label="Código postal" formik={formik} />
                                            <div className="col-span-full">
                                                <RequiredInput id="externalClientContact" type="text" label="Datos de contacto" formik={formik} />
                                            </div>
                                        </>
                                    )}
                                </FormGroup>

                                <FormGroup
                                    title="Retenciones y exenciones"
                                    description="IRPF, IRC y exención de IVA"
                                    className='pt-3 pb-4'
                                    titleCols="@md:col-span-2"
                                    childCols="@md:col-span-10 md:grid-cols-3"
                                >
                                    <RequiredInput
                                        id="irpf"
                                        type="number"
                                        label="IRPF (%)"
                                        suffix={<FaPercent />}
                                        formik={formik}
                                        required={false}
                                    />
                                    <RequiredInput
                                        id="irc"
                                        type="number"
                                        label="IRC (%)"
                                        suffix={<FaPercent />}
                                        formik={formik}
                                        required={false}
                                    />
                                    <div className="col-span-full flex flex-col gap-2 pt-2">
                                        <Switch
                                            id="ivaExempt"
                                            label="IVA no sujeto (Canarias, Ceuta y Melilla)"
                                            checked={formik.values.ivaExempt}
                                            onChange={(e) => formik.setFieldValue('ivaExempt', e.target.checked)}
                                            disabled
                                        />
                                        {formik.values.ivaExempt && (
                                            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                                                <span className="font-medium">Texto legal obligatorio: </span>
                                                {IVA_NON_SUBJECT_LEGAL_TEXT}
                                            </div>
                                        )}
                                    </div>
                                </FormGroup>
                            </>
                        )}
                    </form>
                </div>
            </>
        )
    };

    return (
        <Fragment>
            <Page container="fluid">
                {(mode === "Editar" && data) && getContent()}
                {mode === "Crear" && getContent()}
            </Page>
            <FormFooter
                submitBtnText={mode + ' Factura'}
                handleCancelBtn={() => navigate(-1)}
                handleSubmitBtn={formik.submitForm} isLoading={isLoading}
            />
        </Fragment>
    )
};

export default InvoiceForm;
